import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ListItem,
  ListItemText,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { baseAxios } from "../../utils/config";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import {
  PAYMENT_METHODS,
  getPaymentMethodLabel,
} from "../../utils/paymentMethods";
import {
  amountDue,
  formatMoney,
  parseAppointmentDeposits,
} from "../../utils/appointmentDeposits";
import {
  commitDiscountInput,
  computeDiscountAmount,
  discountLabel,
  formatMoneyInput,
  parseMoneyInput,
  sanitizeMoneyInput,
} from "./checkoutMoney";
import {
  dialogPaperSx,
  dialogTitleSx,
  dialogCloseIconSx,
  primaryBtnSx,
  secondaryBtnSx,
  outlinedBrandBtnSx,
} from "./schedulerDialogStyles";

const CheckoutSummaryModal = ({ open, onClose, onConfirm, appointment }) => {
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState("");
  const [emailAddress, setEmailAddress] = useState(appointment?.client?.client_email || "");
  const [showPaymentFields, setShowPaymentFields] = useState(false);
  const [isEditingCheckout, setIsEditingCheckout] = useState(false);
  const [filteredServicesOptions, setFilteredServicesOptions] = useState([]);
  const [tipAmount, setTipAmount] = useState("");
  const [currentTipAmount, setCurrentTipAmount] = useState("");
  const [selectedServices, setSelectedServices] = useState(
    (appointment?.uniqueServices || []).filter(Boolean)
  );
  const [savedSelectedServices, setSavedSelectedServices] = useState(
    (appointment?.uniqueServices || []).filter(Boolean)
  );
  const [availableServices, setAvailableServices] = useState([]);
  const [deposits, setDeposits] = useState(
    () => parseAppointmentDeposits(appointment?.deposits)
  );
  const [discountType, setDiscountType] = useState("percent");
  const [discountInput, setDiscountInput] = useState("");
  const [savedDiscountType, setSavedDiscountType] = useState("percent");
  const [savedDiscountInput, setSavedDiscountInput] = useState("");
  const receiptRef = useRef(null);
  const trimmedEmailAddress = emailAddress.trim();
  const isValidEmailAddress = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmailAddress),
    [trimmedEmailAddress]
  );

  const serviceTotal = useMemo(() => {
    if (!appointment || !appointment.uniqueServices) return 0;
    return savedSelectedServices.reduce((total, service) => {
      return total + Number(service.servicePrice);
    }, 0.0);
  }, [appointment, savedSelectedServices]);

  const savedDiscountAmount = computeDiscountAmount(
    serviceTotal,
    savedDiscountType,
    savedDiscountInput
  );
  const savedDiscountText = discountLabel(savedDiscountType, savedDiscountInput);
  const tipValue = parseMoneyInput(tipAmount);
  const discountedServiceTotal = Math.max(0, serviceTotal - savedDiscountAmount);
  const subtotalAmount = discountedServiceTotal + tipValue;
  const totalAmount = amountDue(discountedServiceTotal, tipValue, deposits);
  const draftServiceTotal = (selectedServices || []).reduce(
    (total, service) => total + parseMoneyInput(service?.servicePrice),
    0
  );
  const draftDiscountAmount = computeDiscountAmount(
    draftServiceTotal,
    discountType,
    discountInput
  );

  const servicesForOptions = (services) => {
    const chosen = (services || []).filter((service) => service && !service.isDiscount);
    if (!availableServices?.length) {
      return [{ title: "Add new service", service_id: -1 }];
    }
    return availableServices
      .filter(Boolean)
      .filter((option) =>
        chosen.every((service) => service.service_id !== option.service_id)
      );
  };

  const openServiceEditor = () => {
    const services = (savedSelectedServices || []).filter(
      (service) => service && !service.isDiscount
    );
    setShowPaymentFields(false);
    setSelectedServices(services);
    setCurrentTipAmount(sanitizeMoneyInput(tipAmount));
    setDiscountType(savedDiscountType);
    setDiscountInput(savedDiscountInput);
    setFilteredServicesOptions(servicesForOptions(services));
    setIsEditingCheckout(true);
  };

  const checkoutServices = () => {
    const lines = (savedSelectedServices || []).filter(
      (service) => service && !service.isDiscount
    );
    if (savedDiscountAmount <= 0) return lines;
    return [
      ...lines,
      {
        service_id: 0,
        serviceId: 0,
        service_name: savedDiscountText,
        serviceName: savedDiscountText,
        servicePrice: -savedDiscountAmount,
        price: -savedDiscountAmount,
        isDiscount: true,
      },
    ];
  };

  const handleEditingCheckoutSave = () => {
    const normalizedServices = (selectedServices || [])
      .filter((service) => service && !service.isDiscount)
      .map((service) => ({
        ...service,
        servicePrice: formatMoneyInput(service.servicePrice) || "0.00",
      }));
    const committedDiscount = commitDiscountInput(discountType, discountInput);
    setSelectedServices(normalizedServices);
    setSavedSelectedServices(normalizedServices);
    setFilteredServicesOptions(servicesForOptions(normalizedServices));
    const committedTip = formatMoneyInput(currentTipAmount);
    setTipAmount(committedTip);
    setCurrentTipAmount(committedTip);
    setSavedDiscountType(discountType);
    setSavedDiscountInput(committedDiscount);
    setDiscountInput(committedDiscount);
    setIsEditingCheckout(false);
  };

  const handlePrint = async () => {
    const pdfBlob = await generatePdfBlob();
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `receipt-${appointment?.client?.client_name || 'client'}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleConfirm = () => {
    if (!showPaymentFields) {
      setShowPaymentFields(true);
      return;
    }

    if (paymentMethod === "") {
      alert("Please select a payment method.");
      return;
    }

    onConfirm({
      paymentMethod,
      emailAddress,
      savedSelectedServices: checkoutServices(),
      tipAmount: tipValue,
      totalAmount: subtotalAmount,
    });

    handlePrint();
  };

  const handleConfirmEmailOnly = () => {
    if (paymentMethod === "") {
      alert("Please select a payment method method and email address to send.");
      return;
    }
    if (!isValidEmailAddress) {
      alert("Please enter a valid email address to send receipt.");
      return;
    }

    onConfirm({
      paymentMethod,
      emailAddress: trimmedEmailAddress,
      savedSelectedServices: checkoutServices(),
      tipAmount: tipValue,
      totalAmount: subtotalAmount,
    });
  };

  const handleConfirmNoReceipt = () => {
    if (paymentMethod === "") {
      alert("Please select a payment method.");
      return;
    }

    onConfirm({
      paymentMethod,
      emailAddress: "",
      savedSelectedServices: checkoutServices(),
      tipAmount: tipValue,
      totalAmount: subtotalAmount,
    });
  };

  const handleCancel = () => {
    setShowPaymentFields(false);
    setPaymentMethod("");
    setEmailAddress("");
    onClose();
  };

  const loadAvailableServices = async (id) => {
    let filteredServices = [];

    const response = await baseAxios.get(`/services/getUserServices/${id}`);

    if (response?.status === 200) {
      filteredServices = (Array.isArray(response.data) ? response.data : []).filter(
        Boolean
      );
    }

    setAvailableServices(filteredServices);
    setFilteredServicesOptions(
      filteredServices?.length < 1
        ? [{ title: "Add new service", service_id: -1 }]
        : filteredServices.filter(
            (x) =>
              x &&
              appointment.uniqueServices.every(
                (y) => y && y.service_id !== x.service_id
              )
          )
    );
  };

  useEffect(() => {
    loadAvailableServices(appointment?.user_id);
  }, [appointment?.user_id]);

  useEffect(() => {
    if (!appointment?.appointmentId) return undefined;
    let cancelled = false;
    baseAxios
      .get(`/scheduler/deposits/${appointment.appointmentId}`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.deposits)) {
          setDeposits(res.data.deposits);
        }
      })
      .catch(() => {
        // Fall back to deposits already on the scheduler event.
      });
    return () => {
      cancelled = true;
    };
  }, [appointment?.appointmentId]);

  const handleServicesChange = (value) => {
    if (!value) return;
    setSelectedServices((prev) => [...prev, value].filter(Boolean));
    setFilteredServicesOptions((prev) =>
      (prev || []).filter((x) => x && x.service_id !== value.service_id)
    );
  };

  const addNewServiceToStylist = () => {
    navigate({
      pathname: "/add-service-to-Stylist",
      search: `?param=${appointment.user_id}`,
    });
  };

  const handleTipChange = (event) => {
    // Keep the raw entry. Formatting on each keystroke turns "12." into "12.00"
    // and makes the decimal impossible to type.
    setCurrentTipAmount(sanitizeMoneyInput(event.target.value));
  };

  const handleRemoveService = (serviceId) => {
    const toRestore = (availableServices || []).find(
      (x) => x && x.service_id === serviceId
    );
    if (toRestore) {
      setFilteredServicesOptions((prev) => [...(prev || []), toRestore].filter(Boolean));
    }
    setSelectedServices((prev) =>
      (prev || []).filter((x) => x && x.service_id !== serviceId)
    );
  };

  const cancelEditingCheckout = () => {
    const services = (savedSelectedServices || []).filter(
      (service) => service && !service.isDiscount
    );
    setIsEditingCheckout(false);
    setCurrentTipAmount(sanitizeMoneyInput(tipAmount));
    setSelectedServices(services);
    setDiscountType(savedDiscountType);
    setDiscountInput(savedDiscountInput);
    setFilteredServicesOptions(servicesForOptions(services));
  };

  const handleServicePriceChange = (value, serviceId) => {
    const nextPrice = sanitizeMoneyInput(value);
    setSelectedServices((prev) =>
      prev.map((service) =>
        service.service_id === serviceId
          ? { ...service, servicePrice: nextPrice }
          : service
      )
    );
  };

  const handleServicePriceBlur = (serviceId) => {
    setSelectedServices((prev) =>
      prev.map((service) =>
        service.service_id === serviceId
          ? {
              ...service,
              servicePrice: formatMoneyInput(service.servicePrice) || "0.00",
            }
          : service
      )
    );
  };

  const generatePdfBlob = async () => {
    try {
      // Create PDF using jsPDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Set font
      pdf.setFont("helvetica", "normal");

      // Header
      pdf.setFontSize(16);
      pdf.text("*** COPY ***", 105, 20, { align: "center" });

      pdf.setFontSize(12);
      pdf.text(appointment?.salon_name || "", 105, 30, { align: "center" });
      pdf.text(appointment?.salon_address || "", 105, 40, { align: "center" });

      // Date and time
      pdf.setFontSize(10);
      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
      pdf.text(`${currentDate} ${currentTime}`, 20, 55);

      // Client name
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text(appointment?.client?.client_name || "", 20, 70);

      // Table headers
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text("DESCRIPTION", 20, 85);
      pdf.text("PRICE", 150, 85);

      // Draw line under headers
      pdf.line(20, 87, 190, 87);

      let currentY = 95;

      // Services
      pdf.setFont("helvetica", "normal");
      savedSelectedServices?.forEach((service) => {
        // Check if we need a new page
        if (currentY > 270) {
          pdf.addPage();
          currentY = 20;
        }

        pdf.text(service.service_name, 20, currentY);
        pdf.text(`$${service.servicePrice || "0.00"}`, 150, currentY);
        currentY += 8;
      });

      if (savedDiscountAmount > 0) {
        if (currentY > 270) {
          pdf.addPage();
          currentY = 20;
        }
        pdf.text(savedDiscountText, 20, currentY);
        pdf.text(`-$${savedDiscountAmount.toFixed(2)}`, 150, currentY);
        currentY += 8;
      }

      // Tip amount
      if (tipValue > 0) {
        if (currentY > 270) {
          pdf.addPage();
          currentY = 20;
        }
        pdf.text("Tip Amount", 20, currentY);
        pdf.text(`$${tipValue.toFixed(2)}`, 150, currentY);
        currentY += 8;
      }

      deposits.forEach((deposit) => {
        if (currentY > 270) {
          pdf.addPage();
          currentY = 20;
        }
        pdf.text(deposit.label, 20, currentY);
        pdf.text(`-$${Number(deposit.amount).toFixed(2)}`, 150, currentY);
        currentY += 8;
      });

      // Summary section
      currentY += 10;
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }

      // Draw line above summary
      pdf.line(20, currentY, 190, currentY);
      currentY += 8;

      pdf.text("Total Items", 20, currentY);
      pdf.text(savedSelectedServices.length.toString(), 150, currentY);
      currentY += 8;

      pdf.text("Subtotal", 20, currentY);
      pdf.text(`$${totalAmount?.toFixed(2) || "0.00"}`, 150, currentY);
      currentY += 8;

      pdf.text("Tax", 20, currentY);
      pdf.text("$0.00", 150, currentY);
      currentY += 8;

      // Total
      pdf.setFont("helvetica", "bold");
      pdf.text("TOTAL", 20, currentY);
      pdf.text(`$${totalAmount?.toFixed(2) || "0.00"}`, 150, currentY);
      currentY += 8;

      pdf.setFont("helvetica", "normal");
      pdf.text(
        getPaymentMethodLabel(paymentMethod),
        20,
        currentY
      );
      pdf.text(`$${totalAmount?.toFixed(2) || "0.00"}`, 150, currentY);
      currentY += 8;

      pdf.text("Tendered", 20, currentY);
      pdf.text(`$${totalAmount?.toFixed(2) || "0.00"}`, 150, currentY);
      currentY += 8;

      pdf.text("Change", 20, currentY);
      pdf.text("$0.00", 150, currentY);

      // Footer
      currentY += 20;
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }

      pdf.setFont("helvetica", "bold");
      pdf.text("Follow Us On Social Media!", 105, currentY, { align: "center" });
      currentY += 10;

      pdf.setFont("helvetica", "normal");
      pdf.text(
        "Facebook: https://www.facebook.com/pg/jondavidsalonva/posts/",
        20,
        currentY
      );

      // Convert to blob
      const pdfBlob = pdf.output("blob");
      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ ...dialogTitleSx, py: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2, color: "#fff" }}>
            Appointment Summary
          </div>
          <IconButton onClick={handleCancel} sx={dialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {!showPaymentFields ? (
          <>
            <Box mb={1}>
              <div style={{ fontSize: 14, lineHeight: 1.4 }}>
                <span style={{ fontWeight: 600 }}>Client:</span>{" "}
                {appointment?.client?.client_name}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.4, marginTop: 2 }}>
                <span style={{ fontWeight: 600 }}>Stylist:</span>{" "}
                {appointment?.user_name}
              </div>
            </Box>
            <Stack mb={2}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                Services
              </div>

              {isEditingCheckout ? (
                <Stack spacing={1}>
                  {selectedServices?.map((service, index) => (
                    <Stack
                      key={service.service_id}
                      className="service-item"
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderTop: index === 0 ? "" : "1px dashed",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.35)"
                            : "#ccc",
                      }}
                    >
                      <Stack sx={{ flexDirection: "row" }}>
                        <IconButton
                          onClick={() =>
                            handleRemoveService(service.service_id)
                          }
                          sx={{
                            height: 24,
                            color: "red",
                            ":hover": { backgroundColor: "#ff000011" },
                          }}
                        >
                          -
                        </IconButton>
                        <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                          {service.service_name}
                        </div>
                      </Stack>

                      <TextField
                        size="small"
                        value={service.servicePrice ?? ""}
                        onChange={(e) =>
                          handleServicePriceChange(
                            e.target.value,
                            service.service_id
                          )
                        }
                        onBlur={() => handleServicePriceBlur(service.service_id)}
                        placeholder="0.00"
                        sx={{ width: 120 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        inputProps={{
                          inputMode: "decimal",
                        }}
                      />
                      {/* <Typography variant="body2">
                        ${service.servicePrice || 0}
                      </Typography> */}
                    </Stack>
                  ))}
                  {
                    <Autocomplete
                      multiple={false}
                      options={filteredServicesOptions}
                      size="small"
                      value={null}
                      blurOnSelect
                      getOptionLabel={(option) =>
                        option?.title || option?.service_name || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.service_id === value?.service_id
                      }
                      renderOption={(props, data) => {
                        return (
                          <ListItem {...props}>
                            {data.service_id === -1 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "8px",
                                }}
                              >
                                <ListItemText
                                  primary="No Services Found"
                                  secondary="Would you like to add a new service?"
                                />
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={addNewServiceToStylist}
                                >
                                  Add Service
                                </Button>
                              </Box>
                            ) : (
                              data.service_name
                            )}
                          </ListItem>
                        );
                      }}
                      onChange={(e, value) => handleServicesChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select More Services"
                        />
                      )}
                    />
                  }
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Discount</div>
                    <Select
                      size="small"
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value)}
                      sx={{ minWidth: 110 }}
                    >
                      <MenuItem value="percent">Percent</MenuItem>
                      <MenuItem value="amount">Amount</MenuItem>
                    </Select>
                    <TextField
                      size="small"
                      value={discountInput}
                      onChange={(e) =>
                        setDiscountInput(sanitizeMoneyInput(e.target.value))
                      }
                      onBlur={() =>
                        setDiscountInput(
                          commitDiscountInput(discountType, discountInput)
                        )
                      }
                      placeholder={discountType === "percent" ? "10" : "0.00"}
                      sx={{ width: 120 }}
                      InputProps={{
                        startAdornment:
                          discountType === "amount" ? (
                            <InputAdornment position="start">$</InputAdornment>
                          ) : null,
                        endAdornment:
                          discountType === "percent" ? (
                            <InputAdornment position="end">%</InputAdornment>
                          ) : null,
                      }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        setDiscountType("percent");
                        setDiscountInput("10");
                      }}
                      sx={outlinedBrandBtnSx}
                    >
                      10% off
                    </Button>
                    {draftDiscountAmount > 0 && (
                      <div style={{ fontSize: 14 }}>
                        −{formatMoney(draftDiscountAmount)}
                      </div>
                    )}
                  </Stack>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Tip</div>
                    <TextField
                      size="small"
                      value={currentTipAmount}
                      onChange={handleTipChange}
                      onBlur={() =>
                        setCurrentTipAmount(formatMoneyInput(currentTipAmount))
                      }
                      placeholder="0.00"
                      sx={{ width: 120 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      inputProps={{
                        inputMode: "decimal",
                      }}
                    />
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  {savedSelectedServices?.map((service, index) => (
                    <Stack
                      key={service.service_id}
                      className="service-item"
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderTop: index === 0 ? "" : "1px dashed",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.35)"
                            : "#ccc",
                      }}
                    >
                      <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                        {service.service_name}
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                        ${service.servicePrice || 0}
                      </div>
                    </Stack>
                  ))}

                  {savedDiscountAmount > 0 && (
                    <Stack
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderTop: "1px dashed",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.35)"
                            : "#ccc",
                      }}
                    >
                      <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                        {savedDiscountText}
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                        −{formatMoney(savedDiscountAmount)}
                      </div>
                    </Stack>
                  )}

                  {tipValue > 0 && (
                    <Stack
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderTop: "1px dashed",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.35)"
                            : "#ccc",
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600 }}>Tip</div>
                      <div style={{ fontSize: 14 }}>
                        {formatMoney(tipValue)}
                      </div>
                    </Stack>
                  )}

                  {deposits.map((deposit) => (
                    <Stack
                      key={deposit.id}
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderTop: "1px dashed",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.35)"
                            : "#ccc",
                      }}
                    >
                      <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                        {deposit.label}
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                        −{formatMoney(deposit.amount)}
                      </div>
                    </Stack>
                  ))}

                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderTop: "1px dashed",
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.55)"
                          : "black",
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {deposits.length > 0 ? "Amount due" : "Total"}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      ${totalAmount?.toFixed(2) || 0}
                    </div>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </>
        ) : (
          <Box mt={1}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              Payment Details
            </div>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              <span style={{ fontWeight: 700 }}>
                {deposits.length > 0 ? "Amount due:" : "Total:"}
              </span>{" "}
              ${totalAmount.toFixed(2)}
            </div>
            <Box mb={1.5}>
              <Select
                fullWidth
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="" disabled>
                  Select method of payment
                </MenuItem>
                {PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <TextField
              fullWidth
              label="Email Address"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              size="small"
              margin="dense"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        {!showPaymentFields ? (
          <>
            {isEditingCheckout ? (
              <>
                <Button size="small" onClick={cancelEditingCheckout} sx={secondaryBtnSx}>
                  Cancel
                </Button>
                <Button size="small" onClick={handleEditingCheckoutSave} sx={primaryBtnSx}>
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button size="small" onClick={handleCancel} sx={secondaryBtnSx}>
                  Cancel
                </Button>
                <Button size="small" onClick={openServiceEditor} sx={outlinedBrandBtnSx}>
                  Edit
                </Button>
                <Button
                  onClick={handleConfirm}
                  variant="contained"
                  size="small"
                  sx={primaryBtnSx}
                >
                  Confirm
                </Button>
              </>
            )}
          </>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", gap: 1, flexWrap: "wrap" }}
          >
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Button size="small" onClick={openServiceEditor} sx={secondaryBtnSx}>
                Back
              </Button>
              <Button size="small" onClick={handleCancel} sx={secondaryBtnSx}>
                Cancel
              </Button>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}
            >
              <Button size="small" onClick={handleConfirmNoReceipt} sx={outlinedBrandBtnSx}>
                No Receipt
              </Button>
              <Button
                size="small"
                onClick={handleConfirmEmailOnly}
                variant="contained"
                disabled={!isValidEmailAddress}
                sx={primaryBtnSx}
              >
                Email Receipt
              </Button>
              <Button
                size="small"
                onClick={handleConfirm}
                variant="outlined"
                sx={outlinedBrandBtnSx}
              >
                Download Receipt
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogActions>

      {/* Hidden receipt component for printing */}
      <div style={{ display: "none" }}>
        <div ref={receiptRef}>
          <Box sx={{ p: 2, margin: "auto" }}>
            <Typography variant="h6" align="center" gutterBottom>
              *** COPY ***
            </Typography>

            <Typography align="center" variant="subtitle1" gutterBottom>
              {appointment?.salon_name}
            </Typography>

            {/* <Typography align="center" variant="body2" gutterBottom>
            4
          </Typography> */}

            <Typography align="center" variant="body2" gutterBottom>
              {appointment?.salon_address}
            </Typography>

            {/* <Typography align="center" variant="body2" gutterBottom mb={2}>
            Arlington, VA 20598
          </Typography> */}

            {/* <Typography align="center" variant="body2" gutterBottom>
            Transaction: {appointment?.transaction_id || "202504090116"}
          </Typography> */}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 2,
                mb: 1,
              }}
            >
              <Typography variant="body2">
                {new Date().toLocaleDateString()}{" "}
                {new Date().toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Stack
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderTop: "1px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "#000",
                }}
              >
                <Typography variant="body2">DESCRIPTION</Typography>
                <Typography variant="body2">PRICE</Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: "bold", mt: 2 }}>
                {appointment.client?.client_name || ""}
              </Typography>
              {savedSelectedServices?.map((service) => (
                <Box
                  key={service.service_id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2">
                    {service.service_name}
                  </Typography>
                  <Typography variant="body2">
                    ${service.servicePrice || "0.00"}
                  </Typography>
                </Box>
              ))}

              {deposits.map((deposit) => (
                <Box
                  key={deposit.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2">{deposit.label}</Typography>
                  <Typography variant="body2">
                    −{formatMoney(deposit.amount)}
                  </Typography>
                </Box>
              ))}

              {savedDiscountAmount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2">{savedDiscountText}</Typography>
                  <Typography variant="body2">
                    −{formatMoney(savedDiscountAmount)}
                  </Typography>
                </Box>
              )}

              {tipValue > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2">Tip Amount</Typography>
                  <Typography variant="body2">
                    {formatMoney(tipValue)}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "#000",
                pt: 1,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total Items</Typography>
                <Typography variant="body2">
                  {savedSelectedServices.length}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">
                  ${totalAmount?.toFixed(2) || "0.00"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Tax</Typography>
                <Typography variant="body2">$0.00</Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
              >
                <Typography variant="body2">TOTAL</Typography>
                <Typography variant="body2">
                  ${totalAmount?.toFixed(2) || "0.00"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">
                  {getPaymentMethodLabel(paymentMethod)}
                </Typography>
                <Typography variant="body2">
                  ${totalAmount?.toFixed(2) || "0.00"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Tendered</Typography>
                <Typography variant="body2">
                  ${totalAmount?.toFixed(2) || "0.00"}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="body2">Change</Typography>
                <Typography variant="body2">$0.00</Typography>
              </Box>
            </Box>

            <Box
              sx={{
                height: "20px",
                borderBottom: "1px solid",
                borderTop: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "#000",
              }}
            />

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Follow Us On Social Media!
              </Typography>
            </Box>

            <Box sx={{ mt: 2, textAlign: "start" }}>
              <Typography variant="body2">
                Facebook: https://www.facebook.com/pg/jondavidsalonva/posts/
              </Typography>
            </Box>
          </Box>
        </div>
      </div>
    </Dialog>
  );
};

export default CheckoutSummaryModal;
